/**
 * How every predictive-search instance asks Shopify for product-only results.
 * Kept as data so the request shape stays in sync across every instance.
 *
 *  - `resources[type]` — products only; the copied collection/page/query Liquid
 *    branches remain inert so the reference markup stays intact.
 *  - `unavailable_products: last` — sold-out matches sink to the bottom instead
 *    of being dropped.
 *  - `fields` — title and product tags only. Vendor, product type, variant title,
 *    barcode and SKU cannot create a result.
 *
 * @type {Record<string, string>}
 */
const PREDICTIVE_SEARCH_PARAMS = {
  'resources[type]': 'product',
  'resources[options][unavailable_products]': 'last',
  'resources[options][fields]': 'title,tag',
};

const PREDICTIVE_SEARCH_DEBOUNCE = 200;
const RECENT_SEARCHES_KEY = 'rs:recent-searches';
const RECENT_SEARCHES_LIMIT = 5;

function predictiveScopedProductQuery(term) {
  const tokens = (term.slice(0, 200).match(/[\p{L}\p{N}]+/gu) || []).slice(0, 12);
  if (!tokens.length) return '';
  const title = tokens.map((token) => `title:${token}`).join(' ');
  const tags = tokens.map((token) => `tag:${token}`).join(' ');
  return `(${title}) OR (${tags})`;
}

/**
 * Escape a string for safe use as a literal inside a RegExp.
 * @param {string} value
 * @returns {string}
 */
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Escape a string for safe interpolation into innerHTML.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    // The shared SearchForm base debounces the input at 300ms. Retune to a
    // snappier 200ms by handling `input` on the capture phase and stopping it
    // reaching the base's slower bubble-phase listener — so exactly one debounced
    // onChange fires, at 200ms, without touching the shared base file.
    if (this.input) {
      const debouncedChange = debounce((event) => this.onChange(event), PREDICTIVE_SEARCH_DEBOUNCE).bind(this);
      this.input.addEventListener(
        'input',
        (event) => {
          event.stopImmediatePropagation();
          debouncedChange(event);
        },
        true
      );
    }

    this.setupEventListeners();
    this.renderRecentSearches();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    // A submitted query (button, Enter without a selected suggestion) is a real
    // intent worth remembering — persist it for the idle recent-searches row.
    this.input.form.addEventListener('submit', this.rememberSubmittedTerm.bind(this));
    this.input.form.addEventListener('submit', this.constrainFullSearchSubmit.bind(this));
  }

  getQuery() {
    return this.input.value.trim().slice(0, 200);
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showIdle();
      return;
    }

    this.hideIdle();
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  /**
   * Reveal the idle panel (recent + popular searches) and open the dropdown. Only
   * acts when an idle panel is present (the header modal) and the input is empty.
   */
  showIdle() {
    const idle = this.querySelector('[data-predictive-search-idle]');
    if (!idle || this.getQuery().length) return;

    // Nothing worth showing (no recent history AND no popular terms configured):
    // stay closed rather than open an empty panel.
    if (!idle.querySelector('[data-recent-searches]:not([hidden]), [data-popular-searches]')) return;

    idle.hidden = false;
    this.setAttribute('idle', 'true');
    this.setAttribute('open', 'true');
    this.isOpen = true;
  }

  /**
   * Hide the idle panel — called the moment a real query starts so live results
   * take over the panel.
   */
  hideIdle() {
    const idle = this.querySelector('[data-predictive-search-idle]');
    if (idle) idle.hidden = true;
    this.removeAttribute('idle');
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      // Empty input on focus: surface the idle panel (recent + popular searches)
      // rather than an empty dropdown, so there's always somewhere to start.
      this.showIdle();
      return;
    }

    this.hideIdle();

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      const previousMatches = previousTerm
        ? currentButtonText.match(new RegExp(escapeRegExp(previousTerm), 'g'))
        : null;
      if (previousMatches && previousMatches.length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);
      searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      return;
    }

    const searchDeferred = this.dispatchSearchUpdateEvent(searchTerm);

    const requestUrl = new URL(routes.predictive_search_url, window.location.origin);
    requestUrl.searchParams.set('q', searchTerm);
    requestUrl.searchParams.set('section_id', 'predictive-search');
    for (const [key, value] of Object.entries(PREDICTIVE_SEARCH_PARAMS)) {
      requestUrl.searchParams.set(key, value);
    }

    fetch(requestUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        searchDeferred?.resolve({ totalCount: this.getTotalResultCount() });
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          searchDeferred?.reject(error);
          return;
        }
        searchDeferred?.reject(error);
        this.close();
        throw error;
      });
  }

  getTotalResultCount() {
    return parseInt(this.predictiveSearchResults.querySelector('[data-total-results]')?.dataset.totalResults) || 0;
  }

  dispatchSearchUpdateEvent(query) {
    const { SearchUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent) return null;

    const deferred = SearchUpdateEvent.createPromise();
    this.dispatchEvent(
      new SearchUpdateEvent({
        search: { query },
        promise: deferred.promise,
      })
    );
    return deferred;
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.highlightMatchedTerms();
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  /**
   * Bold the matched slice of every product/collection/page result title so the
   * eye lands on the reason a row is there. The `queries` resource ships its own
   * `<mark>`-wrapped `styled_text` from Shopify, so it's skipped here. Matching is
   * case-insensitive on each whitespace-separated word of the current term.
   */
  highlightMatchedTerms() {
    const term = this.getQuery();
    if (!term) return;

    const words = term.split(/\s+/).filter(Boolean).map(escapeRegExp);
    if (!words.length) return;

    const pattern = new RegExp(`(${words.join('|')})`, 'gi');
    const headings = this.predictiveSearchResults.querySelectorAll(
      '.predictive-search__item-heading:not(.predictive-search__item-query-result)'
    );

    for (const heading of headings) {
      const text = heading.textContent;
      // textContent is set as a string, so building innerHTML from an escaped
      // copy keeps this injection-safe while wrapping matches in <mark>.
      heading.innerHTML = escapeHtml(text).replace(pattern, '<mark>$1</mark>');
    }
  }

  /**
   * Persist the just-submitted query (deduped, most-recent first, capped) so the
   * idle state can offer it back. Mirrors the wishlist store's defensive style:
   * any storage failure degrades to a no-op rather than throwing.
   */
  rememberSubmittedTerm() {
    const term = this.getQuery();
    if (!term) return;

    try {
      const existing = PredictiveSearch.readRecentSearches().filter(
        (entry) => entry.toLowerCase() !== term.toLowerCase()
      );
      existing.unshift(term);
      window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(existing.slice(0, RECENT_SEARCHES_LIMIT)));
    } catch (error) {
      /* Storage unavailable (private mode / quota) — recent searches simply
         won't persist. Silent by design. */
    }
  }

  constrainFullSearchSubmit(event) {
    if (event.defaultPrevented) return;
    const displayTerm = this.getQuery();
    const scopedQuery = predictiveScopedProductQuery(displayTerm);
    if (!scopedQuery) {
      event.preventDefault();
      return;
    }

    this.input.value = scopedQuery;
    let displayInput = this.input.form.querySelector('input[name="display_q"]');
    if (!displayInput) {
      displayInput = document.createElement('input');
      displayInput.type = 'hidden';
      displayInput.name = 'display_q';
      this.input.form.appendChild(displayInput);
    }
    displayInput.value = displayTerm;
  }

  /**
   * @returns {string[]} Saved recent queries, newest first. Empty on any failure.
   */
  static readRecentSearches() {
    try {
      const raw = window.localStorage.getItem(RECENT_SEARCHES_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.filter((entry) => typeof entry === 'string') : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Paint the recent-searches row into the idle template placeholder (rendered by
   * predictive-search.liquid). Clicking a pill fills the input and submits — same
   * affordance as the popular-searches pills sitting beside it.
   */
  renderRecentSearches() {
    const container = this.querySelector('[data-recent-searches]');
    if (!container) return;

    const terms = PredictiveSearch.readRecentSearches();
    if (!terms.length) {
      container.hidden = true;
      return;
    }

    const list = container.querySelector('[data-recent-searches-list]');
    if (!list) return;

    list.innerHTML = terms
      .map((term) => {
        const safe = escapeHtml(term);
        const url = new URL(this.input.form.action, window.location.origin);
        url.searchParams.set('q', predictiveScopedProductQuery(term));
        url.searchParams.set('display_q', term);
        url.searchParams.set('type', 'product');
        url.searchParams.set('options[prefix]', 'last');
        return `<li class="predictive-search__popular-item"><a href="${url.href}" class="predictive-search__popular-pill" data-recent-search-pill>${safe}</a></li>`;
      })
      .join('');

    container.hidden = false;
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.hideIdle();
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
